import { api } from "@/redux/api";

import { ToneOfVoiceCapturePayload } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import {
	Conversation,
	ConversationSchema,
	ConversationsSchema,
} from "../../pages/Apollo/schema/get-people";
import {
	ListPerson,
	ListPersonSchedule,
	ListPersonScheduleSchema,
	ListPersonSchema,
} from "../../pages/List/schema/list";
import {
	CampaignAction,
	PendingSchedulesBySenderResponse,
	PendingSchedulesBySenderResponseSchema,
	ReassignSchedulesResponse,
	ReassignSchedulesResponseSchema,
} from "../../pages/Account/schema/pendingSchedules";
import {
	SendTestMessageRequest,
	SendTestMessageRequestSchema,
	SendTestMessageResponse,
	SendTestMessageResponseSchema,
} from "../../pages/Account/schema/sendTestMessage";
import {
	PendingSendCancelResult,
	PendingSendCancelResultSchema,
	PendingSendResponse,
	PendingSendResponseSchema,
} from "../../pages/List/schema/pendingSend";

export interface SendMessagesRequest {
	listPersonId: number;
	threadId?: string;
	messages: {
		message: string;
		subject?: string;
		scheduledInDays?: number;
		senderUserId?: number;
		senderEmailId?: number;
		senderType?: "email" | "linkedin";
		recipientType?: "email" | "linkedin";
		recipientEmail?: string;
		cc?: string[];
		bcc?: string[];
		isLinkedinInvite?: boolean;
		toneOfVoice?: ToneOfVoiceCapturePayload;
	}[];
	/**
	 * Park the LinkedIn messages of a sender whose invitation to the lead is still pending behind
	 * that invitation: they send once it is accepted. Without it the API refuses such a send with
	 * LINKEDIN_INVITATION_PENDING (PREC-4216).
	 */
	waitForInviteAcceptance?: boolean;
}

export interface EditScheduledMessageRequest {
	scheduleId: number;
	message?: string;
	subject?: string | null;
	scheduledFor?: string;
	scheduledInDays?: number;
}

/** The lead a schedule write answered for, by id — see the comment on `cancelSchedule`. */
const invalidatesScheduledLead = (
	result: ListPersonSchedule | undefined
): { type: "ListPerson"; id: number }[] =>
	result ? [{ type: "ListPerson", id: result.listPersonId }] : [];

export const scheduleApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getPendingBySender: builder.query<
			PendingSchedulesBySenderResponse,
			{ userId: number; emailId?: number }
		>({
			query: (params) => ({
				url: `schedule/pending-by-sender`,
				params,
				responseSchema: PendingSchedulesBySenderResponseSchema,
			}),
			providesTags: ["Schedule"],
		}),
		sendMessage: builder.mutation<
			Partial<Conversation> & { listPersonId: number },
			{
				listPersonId: number;
				message: string;
				threadId: string;
				scheduledInDays?: number;
			}
		>({
			query: (body) => ({
				url: `schedule/send-message`,
				method: "POST",
				body,
				responseSchema: ConversationSchema,
			}),
		}),
		sendFollowUpMessages: builder.mutation<
			Partial<Conversation & { listPersonId: number }>[],
			{
				data: {
					listPersonId: number;
					message: string;
					threadId: string;
					scheduledInDays?: number;
				}[];
			}
		>({
			query: (body) => ({
				url: `schedule/send-follow-up-messages`,
				method: "POST",
				body,
				responseSchema: ConversationsSchema,
			}),
		}),
		sendMessages: builder.mutation<Partial<Conversation>[], SendMessagesRequest>({
			query: (body) => ({
				url: `schedule/send-messages`,
				method: "POST",
				body,
				responseSchema: ConversationsSchema,
			}),
		}),
		// Undo-send variant: the API holds the send as a delayed job for
		// undoWindowSeconds; the returned pendingSendId cancels it via
		// cancelPendingSend while the window is open.
		sendMessagesDeferred: builder.mutation<
			PendingSendResponse,
			SendMessagesRequest & { undoWindowSeconds: number }
		>({
			query: (body) => ({
				url: `schedule/send-messages/deferred`,
				method: "POST",
				body,
				responseSchema: PendingSendResponseSchema,
			}),
		}),
		cancelPendingSend: builder.mutation<PendingSendCancelResult, string>({
			query: (pendingSendId) => ({
				url: `schedule/pending-send/${pendingSendId}/cancel`,
				method: "POST",
				responseSchema: PendingSendCancelResultSchema,
			}),
		}),
		toggleSequence: builder.mutation<ListPersonSchedule, number>({
			query: (body) => ({
				url: `schedule/toggle/${body}`,
				method: "POST",
				body,
				responseSchema: ListPersonScheduleSchema,
			}),
		}),
		// The writes to ONE scheduled message — cancel, pause, resume, edit, send now — answer with the
		// row's new state, which `patchInboxSchedule` mirrors into `state.inbox`. The Contacts panel
		// and the extension take their chat lead from the `getSingleListPerson` cache instead, which
		// the slice cannot reach, so each write also invalidates that lead by id (PREC-4220) — by id,
		// so the grid's plain "ListPerson" tag is not refetched.
		cancelSchedule: builder.mutation<ListPersonSchedule, number>({
			query: (id) => ({
				url: `schedule/${id}/cancel`,
				method: "POST",
				responseSchema: ListPersonScheduleSchema,
			}),
			invalidatesTags: invalidatesScheduledLead,
		}),
		// Pause / resume ONE manual follow-up (PREC-2861). Not the lead-level pause (that is
		// `usePauseLeadOutreachMutation`, which holds every campaign step and writes the cut-off) and
		// not the legacy toggle (which flips every row of the lead): the API accepts only an `n99` row.
		pauseSchedule: builder.mutation<ListPersonSchedule, number>({
			query: (id) => ({
				url: `schedule/${id}/pause`,
				method: "POST",
				responseSchema: ListPersonScheduleSchema,
			}),
			invalidatesTags: invalidatesScheduledLead,
		}),
		resumeSchedule: builder.mutation<ListPersonSchedule, number>({
			query: (id) => ({
				url: `schedule/${id}/resume`,
				method: "POST",
				responseSchema: ListPersonScheduleSchema,
			}),
			invalidatesTags: invalidatesScheduledLead,
		}),
		// Send ONE manual scheduled message now (PREC-3103): the API clears its send time and any
		// pause, and the publisher takes it on its next pass.
		sendScheduleNow: builder.mutation<ListPersonSchedule, number>({
			query: (id) => ({
				url: `schedule/${id}/send-now`,
				method: "POST",
				responseSchema: ListPersonScheduleSchema,
			}),
			invalidatesTags: invalidatesScheduledLead,
		}),
		// Edit ONE manual scheduled message in place (PREC-4220): body, subject (mail only) and the
		// timing — `scheduledFor` for a message with a send time, `scheduledInDays` for a follow-up
		// still parked behind a LinkedIn invitation. Only the changed fields travel.
		editScheduledMessage: builder.mutation<ListPersonSchedule, EditScheduledMessageRequest>({
			query: ({ scheduleId, ...body }) => ({
				url: `schedule/${scheduleId}/message`,
				method: "PATCH",
				body,
				responseSchema: ListPersonScheduleSchema,
			}),
			invalidatesTags: invalidatesScheduledLead,
		}),
		reassignSchedules: builder.mutation<
			ReassignSchedulesResponse,
			{ campaignActions: CampaignAction[] }
		>({
			query: (body) => ({
				url: `schedule/reassign`,
				method: "POST",
				body,
				responseSchema: ReassignSchedulesResponseSchema,
			}),
			invalidatesTags: ["Schedule"],
		}),
		updateScheduleSender: builder.mutation<
			ListPerson,
			{ scheduleId: number; userId: number; emailId?: number }
		>({
			query: ({ scheduleId, ...body }) => ({
				url: `schedule/${scheduleId}/sender`,
				method: "PATCH",
				body,
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["Schedule"],
		}),
		sendTestMessage: builder.mutation<SendTestMessageResponse, SendTestMessageRequest>({
			query: (body) => ({
				url: `schedule/send-test-message`,
				method: "POST",
				body,
				requestSchema: SendTestMessageRequestSchema,
				responseSchema: SendTestMessageResponseSchema,
			}),
		}),
	}),
});

export const {
	useGetPendingBySenderQuery,
	useLazyGetPendingBySenderQuery,
	useSendMessageMutation,
	useSendFollowUpMessagesMutation,
	useSendMessagesMutation,
	useSendMessagesDeferredMutation,
	useCancelPendingSendMutation,
	useToggleSequenceMutation,
	useCancelScheduleMutation,
	usePauseScheduleMutation,
	useResumeScheduleMutation,
	useSendScheduleNowMutation,
	useEditScheduledMessageMutation,
	useReassignSchedulesMutation,
	useUpdateScheduleSenderMutation,
	useSendTestMessageMutation,
} = scheduleApi;
export const scheduleApiReducer = scheduleApi.reducer;
export const scheduleApiMiddleware = scheduleApi.middleware;
